import {
  getRanking,
  getRankingHistory,
  getAllValues,
  getDesiredValues,
  removeDesiredValue,
  saveDesiredValue,
  updateDesiredValue,
} from '@/service/culture'

const initialState = () => {
  return {
    ranking: [],
    date: undefined,
    culturalEntropy: undefined,
    culturalAlignment: undefined,
    maturityLevel: undefined,
    history: [],
    historyDates: [],
    values: [],
    desiredValues: [],
    votes: 0,
    loadingDesiredValues: false,
  }
}

let state = initialState()

const getters = {
  ranking: state => state.ranking,
  date: state => state.date,
  culturalEntropy: state => state.culturalEntropy,
  culturalAlignment: state => state.culturalAlignment,
  maturityLevel: state => state.maturityLevel,
  history: state => state.history,
  historyDates: state => state.historyDates,
  values: state => state.values,
  desiredValues: state => state.desiredValues,
  votes: state => state.votes,
  loadingDesiredValues: state => state.loadingDesiredValues,
}

const mutations = {
  setRanking(
    state,
    { votes, ranking, date, culturalEntropy, culturalAlignment, maturityLevel }
  ) {
    state.ranking = ranking
    state.date = date
    state.culturalEntropy = culturalEntropy
    state.culturalAlignment = culturalAlignment
    state.maturityLevel = maturityLevel
    state.votes = votes
  },
  setRankingHistory(state, { history, dates }) {
    state.history = history
    state.historyDates = dates.map(d => {
      const date = d.split('/')
      return `${date[2]}-${date[1]}-${date[0]}`
    })
  },
  setValues(state, { values }) {
    state.values = values
  },
  setDesiredValues(state, { desiredValues }) {
    desiredValues.forEach(v => {
      v.visible = false
      v.loading = false
    })
    state.desiredValues = desiredValues
  },
  setLoadingDesiredValues(state, loadingDesiredValues) {
    state.loadingDesiredValues = loadingDesiredValues
  },
  setLoadingDesiredValue(state, { idx, status }) {
    state.desiredValues[idx].loading = status
  },
  addDesiredValue(state) {
    state.desiredValues.forEach(v => (v.visible = false))
    state.desiredValues.push({
      id: state.desiredValues.length,
      visible: true,
      name: '',
      cultureValues: [],
      isNew: true,
      loading: false,
    })
  },
  toggleVisibilityDesiredValue(state, idx) {
    const desiredValue = state.desiredValues[idx]
    // save state
    const visibility = desiredValue.visible
    // set all to not visible
    state.desiredValues.forEach(v => (v.visible = false))
    // toggle save state
    desiredValue.visible = !visibility
  },
  changeDesiredValueName(state, { idx, name }) {
    state.desiredValues[idx].name = name
  },
  removeDesiredValue(state, idx) {
    state.desiredValues = state.desiredValues.filter(
      (i, index) => idx !== index
    )
  },
  saveDesiredValue(state, { id, name }) {
    const desiredValue = state.desiredValues.find(v => v.name === name)
    desiredValue.id = id
    desiredValue.isNew = false
  },
  changeSelectedValues(state, { idx, values }) {
    state.desiredValues[idx].cultureValues = values
  },
}

const actions = {
  async loadRanking({ commit }, { groupId }) {
    const { data } = await getRanking(groupId)
    commit('setRanking', data)
  },
  async loadRankingHistory({ commit }, { groupId }) {
    const { data } = await getRankingHistory(groupId)
    commit('setRankingHistory', data)
  },
  async loadValues({ commit }) {
    const { data } = await getAllValues()
    commit('setValues', data)
  },
  async loadDesiredValues({ commit }) {
    commit('setLoadingDesiredValues', true)
    return getDesiredValues()
      .then(({ data }) => {
        commit('setDesiredValues', data)
        commit('setLoadingDesiredValues', false)
      })
      .catch(() => {
        commit('setLoadingDesiredValues', false)
      })
  },
  async addDesiredValue({ commit }) {
    commit('addDesiredValue')
  },
  async toggleVisibilityDesiredValue({ commit }, { idx }) {
    commit('toggleVisibilityDesiredValue', idx)
  },
  async changeDesiredValueName({ commit }, value) {
    commit('changeDesiredValueName', value)
  },
  async removeDesiredValue({ commit }, { idx, id, isNew }) {
    if (isNew) {
      return commit('removeDesiredValue', idx)
    }
    commit('setLoadingDesiredValue', { idx, status: true })
    return removeDesiredValue(id)
      .then(() => {
        commit('removeDesiredValue', idx)
      })
      .catch(() => {
        commit('setLoadingDesiredValue', { idx, status: false })
      })
  },
  async saveDesiredValue({ commit }, { idx, name, cultureValues }) {
    commit('setLoadingDesiredValue', { idx, status: true })
    return saveDesiredValue({ desiredValue: { name, cultureValues } })
      .then(({ data }) => {
        commit('saveDesiredValue', data.desiredValue)
        commit('setLoadingDesiredValue', { idx, status: false })
        commit('toggleVisibilityDesiredValue', idx)
      })
      .catch(() => {
        commit('setLoadingDesiredValue', { idx, status: false })
      })
  },
  async updateDesiredValue({ commit }, { id, idx, name, cultureValues }) {
    commit('setLoadingDesiredValue', { idx, status: true })
    return updateDesiredValue({ desiredValue: { id, name, cultureValues } })
      .then(() => {
        commit('setLoadingDesiredValue', { idx, status: false })
        commit('toggleVisibilityDesiredValue', idx)
      })
      .catch(() => {
        commit('setLoadingDesiredValue', { idx, status: false })
      })
  },
  async changeSelectedValues({ commit }, { idx, values }) {
    commit('changeSelectedValues', { idx, values })
  },
}

export default {
  namespaced: true,
  actions,
  getters,
  mutations,
  state,
}
